import { createContext } from 'react';

import { getRoundState } from '../components/_organisms/LiveGame';
import { DEFAULT_AUDIO_VOLUME } from '../hooks/useAudio';
import { Game } from '../services/backendService/types';
import { getRoundCategory } from './roundUtils';

export type MusicState = {
  song?: string;
  isLooping: boolean;
  autoplay: boolean;
  volume?: number;
};

export const GameMusicContext = createContext<{
  music: MusicState;
  setMusic: React.Dispatch<React.SetStateAction<MusicState>>;
}>({
  music: {
    isLooping: true,
    autoplay: true,
    song: undefined,
    volume: DEFAULT_AUDIO_VOLUME,
  },
  setMusic: () => undefined,
});
export const musicMapper = {
  lobby: {
    song: '/sum_of_funk.wav',
    loop: true,
  },
  ask: {
    song: '/questions_blazer_rail.wav',
    loop: true,
  },
  idle: {
    song: '/end_of_questions.wav',
    loop: false,
  },
  vote: {
    song: '/voting_waiting_game.wav',
    loop: true,
  },
  leaderboard: {
    song: '/leaderboard.wav',
    loop: false,
  },
};

export const findRoundSong = (game: Game, now: () => number, showLobby: boolean) => {
  const slideState = getRoundState({ game, now });

  const slide = game.slides?.find(slide => slide.id === game.currentRound);

  if (showLobby && slideState !== 'in' && slideState !== 'countdown')
    return musicMapper.lobby;

  if (!showLobby && slideState === 'pre') return null;
  if (!showLobby && slideState === 'post-between') return musicMapper.idle;

  const isInSlideCountdown =
    game.isPlaying &&
    slideState === 'in' &&
    game.roundStartsAt &&
    slide?.durationSeconds &&
    new Date(game.roundStartsAt + slide?.durationSeconds * 1000).getTime() > now();

  const slideCategory = getRoundCategory(slide?.type);

  if (isInSlideCountdown && slideCategory === 'response') {
    return musicMapper.ask;
  }

  const isInPreRoundCountdown =
    game.isPlaying &&
    slideState === 'countdown' &&
    game.roundStartsAt &&
    game.roundStartsAt > now();

  if (isInPreRoundCountdown && slideCategory === 'response') return musicMapper.ask;

  if (isInPreRoundCountdown && slideCategory === 'collaboration') return musicMapper.vote;

  if (game.isPlaying && slideCategory === 'results' && !slide?.wasPlayed) {
    return musicMapper.leaderboard;
  }

  if (game.isPlaying && slideState === 'pre') {
    // prevent playing music when moving from lobby straight to activity
    return null;
  }
  if (game.isPlaying && slideCategory === 'results' && slide?.wasPlayed) {
    // prevent playing music when the round has been played
    return null;
  }

  if (game.isPlaying && slideState === 'post' && !game.roundStartsAt) {
    // prevent playing music when navigating between slides
    return null;
  }
  if (game.isPlaying && slideState === 'post') return musicMapper.idle;

  // keep playing
  return undefined;
};

export const calculateVolume = (vol: number) => {
  const cubicVolume = Math.pow(vol, 3);

  return cubicVolume;
};
